import { useEffect, useMemo, useState } from "react";
import { Phone, Globe, MapPin } from "lucide-react";
import { api } from "../../lib/api";
import { useBrand } from "../../contexts/BrandContext";
import SEO from "../../components/SEO";

export default function Dealers() {
  const { mode, brand } = useBrand();
  const [dealers, setDealers] = useState([]);
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");

  useEffect(() => {
    const load = async () => {
      const params = new URLSearchParams();
      if (mode === "brand" && brand) params.set("brand_slug", brand.slug);
      if (city) params.set("city", city);
      if (district) params.set("district", district);
      const { data } = await api.get(`/dealers?${params.toString()}`);
      setDealers(data);
    };
    load();
  }, [mode, brand, city, district]);

  // For city/district options derive from all dealers
  const [allDealers, setAllDealers] = useState([]);
  useEffect(() => {
    const params = new URLSearchParams();
    if (mode === "brand" && brand) params.set("brand_slug", brand.slug);
    api.get(`/dealers?${params.toString()}`).then((r) => setAllDealers(r.data));
  }, [mode, brand]);

  const cities = useMemo(() => [...new Set(allDealers.map((d) => d.city).filter(Boolean))].sort(), [allDealers]);
  const districts = useMemo(
    () =>
      [
        ...new Set(
          allDealers.filter((d) => !city || d.city === city).map((d) => d.district).filter(Boolean)
        ),
      ].sort(),
    [allDealers, city]
  );

  return (
    <div className="fade-in-up" data-testid="dealers-page">
      <SEO title="Yetkili Bayiler" />
      <div className="border-b border-white/10 py-16 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Network</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter">Yetkili Bayiler</h1>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            Türkiye genelindeki yetkili bayilerimizden destek alabilirsiniz.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row gap-4 mb-10">
          <select
            value={city}
            onChange={(e) => {
              setCity(e.target.value);
              setDistrict("");
            }}
            className="bg-[#0A0A0A] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-white/30 min-w-[200px]"
            data-testid="dealers-city-filter"
          >
            <option value="">Tüm İller</option>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
            className="bg-[#0A0A0A] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-white/30 min-w-[200px]"
            data-testid="dealers-district-filter"
          >
            <option value="">Tüm İlçeler</option>
            {districts.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        {dealers.length === 0 ? (
          <div className="text-center py-20 text-zinc-500" data-testid="dealers-empty">
            Bayi bulunmuyor.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="dealers-grid">
            {dealers.map((d) => (
              <div
                key={d.id}
                className="border border-white/10 bg-[#0A0A0A] p-6 hover:border-white/30 transition-colors"
                data-testid={`dealer-card-${d.id}`}
              >
                <div className="font-display text-lg font-medium mb-1">{d.name}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                  {d.city} {d.district && `· ${d.district}`}
                </div>
                {d.address && (
                  <div className="flex items-start gap-2 text-sm text-zinc-400 mb-2">
                    <MapPin size={14} className="mt-0.5 shrink-0" /> {d.address}
                  </div>
                )}
                {d.phone && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                    <Phone size={14} /> {d.phone}
                  </div>
                )}
                {d.website && (
                  <a
                    href={d.website.startsWith("http") ? d.website : `https://${d.website}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-sm hover:underline"
                    style={{ color: "var(--brand-primary)" }}
                  >
                    <Globe size={14} /> Web Sitesi
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
